import { useState, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import { TableCard, TableSkeleton, TableEmpty, TableError, SearchInput, Pager, FilterBar, TH, TD, ROW, usePagination, PageSizeSelect, RECORD_PAGE_SIZE, useSort, SortableTH, FILTER_BTN, FilterCount, FilterActions } from './tableUi';

/** Cameras order by name; there is no time on this table. */
const CAMERA_SORTS = { name: (c: any) => c.name || c.location || c.id || '' };

const ROLES = ['generic', 'entry', 'exit', 'both'];

/** UC3 — designate cameras as entry/exit; single camera uses the event direction flag. */
export interface CameraConfigTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
}

export default function CameraConfigTab({ nodeKey = '' }: CameraConfigTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const canConfig = hasPermission('attendance.config'); // backend also enforces
  const [cameras, setCameras] = useState<any[]>([]);
  const [cfgByCam, setCfgByCam] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cams = await attendance.listCameras();
      const cfgs = (await attendance.listCameraConfigs()) || [];
      const map: Record<string, any> = {};
      cfgs.forEach((c: any) => { map[c.camera_id] = c; });
      setCameras(Array.isArray(cams) ? cams : []);
      setCfgByCam(map);
      setError('');
    } catch (e: any) {
      console.error('camera config load failed', e);
      setError(e?.message || 'Request failed');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey]);

  useEffect(() => { load(); }, [load]);

  const update = (camId: string, patch: any) => setCfgByCam((m) => ({ ...m, [camId]: { ...(m[camId] || {}), camera_id: camId, ...patch } }));

  const save = async (camId: string) => {
    setSavingId(camId);
    try {
      const c = cfgByCam[camId] || { role: 'generic' };
      const saved = await attendance.upsertCameraConfig(camId, c);
      update(camId, saved);
    } catch (e) { console.error(e); } finally { setSavingId(''); }
  };

  // Nodes routinely carry dozens of cameras; without a search, assigning an entry/exit
  // role meant paging until the right name appeared.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cameras;
    return cameras.filter((c) => [c.name, c.location, c.id].some((v) => (v || '').toLowerCase().includes(q)));
  }, [cameras, search]);

  const { sorted, sort, toggleSort } = useSort(filtered, CAMERA_SORTS);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(sorted, RECORD_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, sort, setPage]);

  return (
    <div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search camera…" label="Search cameras" />
        {search && (
          <button onClick={() => setSearch('')} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}
        <FilterCount>
          {search ? `${filtered.length} of ${cameras.length}` : `${cameras.length}`} camera{cameras.length !== 1 ? 's' : ''}
        </FilterCount>
        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button data-tour="attendance-config-refresh" onClick={load} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </FilterActions>
      </FilterBar>
      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error && cameras.length === 0 ? (
          <TableError message={error} onRetry={load}>Couldn&apos;t load cameras</TableError>
        ) : cameras.length === 0 ? (
          <TableEmpty>No cameras on this node.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No cameras match this search.</TableEmpty>
        ) : (
          <>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <SortableTH label="Camera" columnKey="name" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Role</th>
                  <th className={TH}>Use direction</th>
                  <th className={TH}>IN direction</th>
                  <th className={TH}>OUT direction</th>
                  <th className={`${TH} text-right`}></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((cam) => {
                  const c = cfgByCam[cam.id] || { role: 'generic', use_direction: false };
                  const both = c.role === 'both';
                  return (
                    <tr key={cam.id} className={ROW}>
                      <td className={TD}><div className="font-medium text-foreground">{cam.name}</div><div className="text-xs text-muted-foreground">{cam.location || cam.id}</div></td>
                      <td className={TD}>
                        <select value={c.role || 'generic'} onChange={(e) => update(cam.id, { role: e.target.value })} className="px-2 py-1 text-sm rounded border border-border bg-background text-foreground">
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td className={TD}><input type="checkbox" disabled={!both} checked={!!c.use_direction} onChange={(e) => update(cam.id, { use_direction: e.target.checked })} className="h-4 w-4 accent-[var(--brand)] disabled:opacity-40" /></td>
                      <td className={TD}><input disabled={!both || !c.use_direction} placeholder="e.g. Top-to-Bottom" value={c.in_direction || ''} onChange={(e) => update(cam.id, { in_direction: e.target.value })} className="px-2 py-1 text-sm rounded border border-border bg-background text-foreground disabled:opacity-40 w-40" /></td>
                      <td className={TD}><input disabled={!both || !c.use_direction} placeholder="e.g. Bottom-to-Top" value={c.out_direction || ''} onChange={(e) => update(cam.id, { out_direction: e.target.value })} className="px-2 py-1 text-sm rounded border border-border bg-background text-foreground disabled:opacity-40 w-40" /></td>
                      <td className={`${TD} text-right`}>{canConfig && <button onClick={() => save(cam.id)} disabled={savingId === cam.id} className="px-2 py-1 rounded text-xs bg-brand text-white hover:bg-brand-dark disabled:opacity-50">{savingId === cam.id ? '…' : 'Save'}</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>
    </div>
  );
}
